import React, { useEffect, useRef, useState } from "react";
import LexicalEditor from "../../../common/components/text-editor/text-editor";
import { Box } from "@mui/material";
import "./header-tab.scss";
import {
  editTemplateHeaderOrFooter,
  getTemplateHeaderOrFooter,
} from "../../../api/email-letter-templates";
import { AlertComponent } from "../../../common/components";
import { $getRoot, $insertNodes} from "lexical";
import { $generateNodesFromDOM } from "@lexical/html";
import { validateMergeTokens } from "../email-letter-helper";

const HeaderTab: React.FC = () => {
  const [content, setContent] = React.useState<string>("");
  const [defaultHtml, setDefaultHtml] = React.useState<any>("");
  const [alertMessage, setAlertMessage] = React.useState<any>({
    open: false,
    message: "",
  });
  const [contentError, setContentError] = React.useState<string>("");
  const [invalidMergeTokensError, setInvalidMergeTokensError] = useState("");

  const callValidateMergeTokens = (htmlString: string) => {
    setInvalidMergeTokensError('');
    const isValidBody = validateMergeTokens(htmlString);
    if (!isValidBody) {
      setInvalidMergeTokensError(
        `Header contains invalid or incomplete merge tokens`
      );
    }
    return isValidBody;
  };

  const editorRef = useRef<any>(null);

  const handleEditorInit = (editorInstance: any) => {
    editorRef.current = editorInstance;
  };

  const handleDiscard = () => {
    if (editorRef?.current) {
      const htmlString = defaultHtml;

      const parser = new DOMParser();
      const dom = parser.parseFromString(htmlString, "text/html");

      editorRef.current.update(() => {
        const root = $getRoot();
        root.clear(); // Clear existing content

        const nodes = $generateNodesFromDOM(editorRef.current, dom);
        $insertNodes(nodes);
      });
    }
  };

  const getHeaderContent = async () => {
    try {
      const res = await getTemplateHeaderOrFooter("Header");
      const data = res?.data?.data;
      const headerData = data?.length
        ? data?.filter((item: any) => item.key === "Header")?.[0]
        : null;
      console.log("first", data, headerData);
      if (headerData) {
        setDefaultHtml(headerData.value);
      }
      console.log("Header content fetched successfully:", res);
    } catch (error: any) {
      console.error("Error fetching header content:", error);
    }
  };

  const editHeaderContent = async () => {
    try {
      // Validation: block if %%Header%% is present
      if (/%%header%%/i.test(content) || /%%footer%%/i.test(content)) {
        setContentError("You cannot include %%Header%% or %%Footer%% inside the Email Header content.");
        return;
      }
      setContentError('')
      if (content && !callValidateMergeTokens(content)) {
        return;
      }
      const res = await editTemplateHeaderOrFooter("Header", {
        value: content,
      });
      setAlertMessage({
        open: true,
        message: `Header Saved Successfully.`,
        type: "success",
      });
      console.log("Header content edited successfully:", res);
    } catch (error: any) {
      setAlertMessage({
        open: true,
        message:
          error?.response?.data?.message || "Failed to save header template.",
        type: "error",
      });
      console.error("Error editing header content:", error);
    }
  };

  useEffect(() => {
    getHeaderContent();
  }, []);

  return (
    <Box className="header-tab-container">
      <p className="email-header-description">
        Whenever we see <b>%%header%%</b> in your Email Templates, we'll replace it
        with the content below:
      </p>
      {invalidMergeTokensError &&
        <div className="template-body-validation-error-message header-error">
          {invalidMergeTokensError}
        </div>
      }
      <div className="header-tab">
        <p className="email-header-title">Email Header</p>
        <LexicalEditor
          handleDataChange={(html: any, children: any) => {
            setInvalidMergeTokensError('');
            setContent(html);
            setContentError('');
          }}
          defaultHtmlJson={defaultHtml}
          onEditorInit={handleEditorInit}
          fieldName="Header"
        />
        {
          contentError && (
            <div className="error-message">{contentError}</div>
          )
        }
        <Box className="editor-actions">
          <Box gap={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              className="cancel-button"
              onClick={() => {
                setContent(defaultHtml);
                handleDiscard();
                console.log("Cancel button clicked");
                // Optionally, you can reset the content or perform other actions
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={editHeaderContent}
              className="cancel-button next-button-wrapper"
            >
              Save
            </button>
          </Box>
        </Box>
      </div>

      {alertMessage?.open && (
        <AlertComponent
          message={alertMessage?.message}
          type={alertMessage?.type || "info"}
          open={alertMessage?.open || false}
          onClose={() => setAlertMessage({ ...alertMessage, open: false })}
        />
      )}
    </Box>
  );
};

export default HeaderTab;
